import React, {useMemo, useState, useEffect} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {Card, Alert} from 'react-bootstrap';
import {PageTitle} from '../../../../_metronic/layout/core';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import {ColumnDef} from '@tanstack/react-table';
import DeleteMemberModal from './modals/DeleteMemberModal';
import AddMemberModal from './modals/AddMemberModal';
import styles from '../../Styles/Email-accounts/DomainMailingListMembers.module.scss';
import clsx from 'clsx';
import {isNotFoundError, NOT_FOUND_ROUTE} from '../../../../app/common/tools/httpError';
import {addAddressMailingList, deleteAddressMailingList, getMailinglists} from '../../Api/domainEmailAccounts';

interface Member {
  id: number;
  email: string;
}

interface MailingList {
  id: number;
  adresse: string;
  membres?: string[];
}

interface LocationState {
  mailingList?: MailingList;
  [key: string]: any;
}

interface AlertMessage {
  type: 'success' | 'danger';
  message: string;
}

interface MailingListResponse {
  name: string;
  addresses: string[];
}

const DomainMailingListMembers = (): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {domainName, listId} = useParams<{domainName: string; listId: string}>();
  const navigate = useNavigate();
  const location = useLocation();

  const mailingList = (location.state as LocationState)?.mailingList;

  const [members, setMembers] = useState<Member[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState<AlertMessage | null>(null);

  // get members list
  useEffect(() => {
    const fetchMembers = async () => {
      if (domainName && mailingList?.adresse) {
        setIsLoading(true);
        try {
          const response = await getMailinglists(domainName);

          const mailingLists: MailingListResponse[] = response || [];
          const currentList = mailingLists.find(
            (list: MailingListResponse) => list.name === mailingList.adresse
          );

          if (currentList?.addresses && Array.isArray(currentList.addresses)) {
            const membersData = currentList.addresses.map((email: string, index: number) => ({
              id: index + 1,
              email,
            }));
            setMembers(membersData);
          } else {
            setMembers([]);
            console.log('Aucun membre trouvé');
          }
        } catch (error: any) {
          if (isNotFoundError(error)) {
            navigate(NOT_FOUND_ROUTE, {replace: true});
            return;
          }
          console.error('Erreur lors de la récupération des membres:', error);
          setAlert({
            type: 'danger',
            message: error?.response?.data?.message || 'Erreur lors de la récupération des membres'
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchMembers();
  }, [domainName, mailingList?.adresse, navigate]);


  const handleBack = () => {
    console.log("location.search", location.search);
    navigate(`/services/domain/${domainName}/mailing-lists${location.search}`);
  };

  const handleDeleteClick = (member: Member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    console.log("domainName: ", domainName, " mailingList: ", mailingList, " memberToDelete: ", memberToDelete);
    if (memberToDelete && domainName && mailingList?.adresse) {
      setIsLoading(true);
      setAlert(null);
      try {
        await deleteAddressMailingList(
          domainName,
          mailingList.id,
          memberToDelete.email
        );

        // Rafraîchir la liste des membres depuis le serveur
        const response = await getMailinglists(domainName);
        const mailingLists: MailingListResponse[] = response || [];
        const currentList = mailingLists.find(
          (list: MailingListResponse) => list.name === mailingList.adresse
        );

        if (currentList?.addresses && Array.isArray(currentList.addresses)) {
          const membersData = currentList.addresses.map((email: string, index: number) => ({
            id: index + 1,
            email,
          }));
          setMembers(membersData);
        } else {
          setMembers([]);
        }

        setAlert({type: 'success', message: 'Membre supprimé avec succès'});
        console.log('Membre supprimé et liste rafraîchie');
      } catch (error: any) {
        console.error('Erreur lors de la suppression du membre:', error);
        setAlert({
          type: 'danger',
          message: error?.response?.data?.message || 'Erreur lors de la suppression du membre'
        });
      } finally {
        setIsLoading(false);
        setShowDeleteModal(false);
      }
    }
  };

  const handleAddMember = async (email: string) => {
    if (domainName && mailingList?.adresse) {
      setIsLoading(true);
      setAlert(null);
      try {
        const mailingListLocalPart = mailingList.adresse.split('@')[0];
        await addAddressMailingList(domainName, {
          name: mailingListLocalPart,
          address: email,
          id: listId
        });

        // Rafraîchir la liste des membres depuis le serveur
        const response = await getMailinglists(domainName);
        const mailingLists: MailingListResponse[] = response || [];
        const currentList = mailingLists.find(
          (list: MailingListResponse) => list.name === mailingList.adresse
        );

        if (currentList?.addresses && Array.isArray(currentList.addresses)) {
          const membersData = currentList.addresses.map((email: string, index: number) => ({
            id: index + 1,
            email,
          }));
          setMembers(membersData);
        } else {
          setMembers([]);
        }

        setAlert({type: 'success', message: 'Membre ajouté avec succès'});
        console.log('Membre ajouté et liste rafraîchie');
        setShowAddModal(false);
      } catch (error: any) {
        console.error('Erreur lors de l\'ajout du membre:', error);
        setAlert({
          type: 'danger',
          message: error?.response?.data?.message || 'Erreur lors de l\'ajout du membre'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };



  const columns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        header: 'Membres de la liste',
        accessorKey: 'email',
        cell: (info) => (
          <span className="fw-bold">{info.getValue() as string}</span>
        ),
      },
      {
        header: 'Actions',
        accessorKey: 'id',
        cell: (info) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light-danger"
              onClick={() => handleDeleteClick(info.row.original)}
              title="Supprimer"
              disabled={isLoading}
            >
              <i className="fa-solid fa-trash fs-6"></i>
            </button>
          </div>
        ),
      },
    ],
    [isLoading]
  );

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {`Membres de ${mailingList?.adresse || 'Liste de diffusion'}`}
      </PageTitle>
        <div className={clsx(styles.containerDomainMailingListMembres)}>
          {alert && (
            <Alert
              variant={alert.type}
              dismissible
              onClose={() => setAlert(null)}
              className="mb-4"
            >
              {alert.message}
            </Alert>
          )}

          <Card>
            <Card.Header>
              <Card.Title className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn btn-sm btn-icon btn-light"
                    onClick={handleBack}
                    title="Retour"
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                  </button>
                  <Level3Title
                    title={`Liste des membres de la liste de diffusion`}
                  />
                </div>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowAddModal(true)}
                  disabled={isLoading}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Ajouter un membre
                </button>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <TableMetronic<Member>
                data={members}
                columns={columns}
                isLoading={isLoading}
                showCardHeader={false}
                searchBar={false}
                pagination
                defaultSorting={[
                  {
                    id: 'email',
                    desc: false,
                  },
                ]}
              />
            </Card.Body>
          </Card>

          {/* Modal de suppression */}
          <DeleteMemberModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            member={memberToDelete}
            mailingListAddress={mailingList?.adresse}
          />

          {/* Modal d'ajout */}
          <AddMemberModal
            show={showAddModal}
            onHide={() => setShowAddModal(false)}
            onAdd={handleAddMember}
            mailingListAddress={mailingList?.adresse}
          />
        </div>
    </>
  );
};

export default DomainMailingListMembers;
